import {
  Box,
  Flex,
  FlexProps,
  HStack,
  IconButton,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { SendLegacyMessage, SendWrappedMessage } from "applesauce-actions/actions";
import { isGiftWrapUnlocked, unlockGiftWrap } from "applesauce-common/helpers";
import { GiftWrapsModel } from "applesauce-common/models";
import { getDisplayName } from "applesauce-core/helpers";
import { getTagValue, unixNow, mergeRelaySets } from "applesauce-core/helpers";
import { useActionRunner, useActiveAccount, useEventModel, useObservableEagerState } from "applesauce-react/hooks";
import { kinds } from "nostr-tools";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { lastValueFrom, toArray } from "rxjs";
import { LuSend } from "react-icons/lu";

import InsertGifButton from "../../../../components/gif/insert-gif-button";
import EyeOff from "../../../../components/icons/eye-off";
import Lock01 from "../../../../components/icons/lock-01";
import MagicTextArea, { RefType } from "../../../../components/magic-textarea";
import InsertReactionButton from "../../../../components/reactions/insert-reaction-button";
import useCacheForm from "../../../../hooks/use-cache-form";
import useTextAreaUploadFile, { useTextAreaInsertTextWithForm } from "../../../../hooks/use-textarea-upload-file";
import { useUserInbox } from "../../../../hooks/use-user-mailboxes";
import { DirectMessageRelaysModel, GroupMessageInboxesModel } from "../../../../models/messages";
import { usePublishEvent } from "../../../../providers/global/publish-provider";
import { eventStore } from "../../../../services/event-store";
import localSettings from "../../../../services/preferences";
import ExpirationToggleButton from "../../components/expiration-toggle-button";

export type MessageType = "nip04" | "nip17";

export default function SendMessageForm({
  pubkey,
  rootId,
  initialType = "nip04",
  initialExpiration,
  ...props
}: { pubkey: string; rootId?: string; initialType?: MessageType; initialExpiration?: number } & Omit<
  FlexProps,
  "children"
>) {
  const account = useActiveAccount()!;
  const publish = usePublishEvent();
  const actions = useActionRunner();
  const toast = useToast();
  const defaultMessageExpiration = useObservableEagerState(localSettings.defaultMessageExpiration);

  const [expiration, setExpiration] = useState<number | null>(initialExpiration ?? defaultMessageExpiration);
  const [type, setType] = useState<MessageType>(initialType);
  const [sending, setSending] = useState(false);

  // For auto-decrypting own messages after send
  const locked = useEventModel(GiftWrapsModel, [account.pubkey, true]);

  useEffect(() => {
    setType(initialType);
  }, [initialType]);
  useEffect(() => {
    if (initialExpiration) setExpiration(initialExpiration);
  }, [initialExpiration]);

  const { getValues, setValue, watch, handleSubmit, formState, reset } = useForm({
    defaultValues: { content: "" },
    mode: "all",
  });
  watch("content");

  const clearCache = useCacheForm<{ content: string }>(`dm-${pubkey}`, getValues, reset, formState, {
    clearOnKeyChange: true,
  });

  const autocompleteRef = useRef<RefType | null>(null);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const insertText = useTextAreaInsertTextWithForm(autocompleteRef, getValues, setValue);
  const { onPaste } = useTextAreaUploadFile(insertText);

  const otherInboxes = useUserInbox(pubkey);
  const selfInboxes = useUserInbox(account.pubkey);
  const conversationId = [account.pubkey, pubkey].sort().join(",");
  const inboxes = useEventModel(GroupMessageInboxesModel, [conversationId]);

  const sendMessage = handleSubmit(async (values) => {
    if (!values.content) return;
    setSending(true);

    try {
      const expirationTimestamp = expiration ? unixNow() + expiration : undefined;

      if (type === "nip04") {
        const events = await lastValueFrom(
          actions.exec(SendLegacyMessage, pubkey, values.content, { expiration: expirationTimestamp }).pipe(toArray()),
        );
        for (const event of events) {
          await publish("Send message", event, mergeRelaySets(otherInboxes, selfInboxes), false, true);
        }
      } else {
        if (!inboxes) throw new Error("Missing inbox relays");

        const events = await lastValueFrom(
          actions
            .exec(SendWrappedMessage, [account.pubkey, pubkey], values.content, { expiration: expirationTimestamp })
            .pipe(toArray()),
        );
        for (const e of events) {
          const p = getTagValue(e, "p");
          if (!p) continue;
          const relays = inboxes?.[p];
          const profile = eventStore.getReplaceable(kinds.Metadata, p);
          const label = `Send to ${getDisplayName(profile)}`;
          await publish(label, e, relays ?? [], false, relays ? true : false);
        }

        // Auto-decrypt own gift wraps so the sent message shows immediately
        setTimeout(async () => {
          try {
            const currentLocked = locked ?? [];
            for (const gw of currentLocked) {
              if (!isGiftWrapUnlocked(gw)) {
                await unlockGiftWrap(gw, account);
              }
            }
          } catch {}
        }, 500);
      }

      clearCache();
      reset({ content: "" });
      setTimeout(() => textAreaRef.current?.focus(), 50);
    } catch (e) {
      toast({ title: "Failed to send", description: String(e), status: "error", duration: 5000 });
    } finally {
      setSending(false);
    }
  });

  const toggleType = useCallback(() => {
    setType((t) => (t === "nip04" ? "nip17" : "nip04"));
  }, []);

  const formRef = useRef<HTMLFormElement | null>(null);
  const content = getValues().content;

  return (
    <Flex
      as="form"
      onSubmit={sendMessage}
      ref={formRef}
      direction="column"
      gap="1"
      borderTopWidth="1px"
      {...props}
    >
      {/* Input row */}
      <Flex gap="2" align="flex-end">
        <MagicTextArea
          flex="1"
          value={content}
          onChange={(e) => setValue("content", e.target.value, { shouldDirty: true, shouldTouch: true })}
          rows={1}
          isRequired
          instanceRef={(inst) => (autocompleteRef.current = inst)}
          ref={textAreaRef}
          onPaste={onPaste}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "Enter" && formRef.current) formRef.current.requestSubmit();
          }}
          placeholder="Message..."
          borderRadius="xl"
          resize="none"
          minH="40px"
          maxH="120px"
        />
        <IconButton
          type="submit"
          aria-label="Send"
          icon={<LuSend />}
          colorScheme="primary"
          borderRadius="full"
          isLoading={sending}
          isDisabled={!content.trim()}
          size="md"
        />
      </Flex>

      {/* Toolbar row */}
      <HStack spacing="0" fontSize="xs">
        <Tooltip label={type === "nip17" ? "Private (NIP-17)" : "Legacy (NIP-04)"} placement="top">
          <IconButton
            aria-label="Toggle encryption"
            icon={type === "nip04" ? <EyeOff boxSize={4} /> : <Lock01 boxSize={4} />}
            size="xs"
            variant="ghost"
            colorScheme={type === "nip04" ? "orange" : "green"}
            onClick={toggleType}
          />
        </Tooltip>
        <ExpirationToggleButton value={expiration} onChange={setExpiration} size="xs" variant="ghost" />
        <InsertGifButton onSelectURL={insertText} aria-label="GIF" size="xs" />
        <InsertReactionButton onSelect={insertText} aria-label="Emoji" size="xs" />
      </HStack>
    </Flex>
  );
}
